import { StreamingExportProgress } from '../types/Streaming';

/**
 * The agent runs in the main process but its streaming work happens here in the
 * renderer, so progress has nowhere to surface on its own. This is the hand-off
 * between the streaming bridge doing the export and the chat drawing it.
 */

type Listener = (progress: StreamingExportProgress | null) => void;

/** How long a finished transfer stays on screen before it clears itself. */
const LINGER_MS = 6000;

const listeners = new Set<Listener>();
let current: StreamingExportProgress | null = null;
let clearTimer: ReturnType<typeof setTimeout> | null = null;

const emit = () => {
  for (const listener of listeners) listener(current);
};

const cancelClear = () => {
  if (clearTimer) clearTimeout(clearTimer);
  clearTimer = null;
};

export const clearAgentTransfer = (): void => {
  cancelClear();
  current = null;
  emit();
};

export const publishAgentTransfer = (
  progress: StreamingExportProgress,
): void => {
  cancelClear();
  current = progress;
  emit();

  if (progress.phase === 'done' || progress.phase === 'error') {
    clearTimer = setTimeout(clearAgentTransfer, LINGER_MS);
  }
};

export const getAgentTransfer = (): StreamingExportProgress | null => current;

export const subscribeToAgentTransfer = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
